import {Component, computed, OnInit} from '@angular/core';
import {ManageTaBaseComponent} from "../manage-ta-base-component.directive";
import {Button} from "primeng/button";
import {TaCostBubbleComponent} from "../../ta-cost-bubble/ta-cost-bubble.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Textarea} from "primeng/textarea";
import {DatePipe} from "@angular/common";
import {TaApprovalsTableComponent} from '../ta-approvals-table/ta-approvals-table.component';
import {SubmissionApprovalDetails} from '../../../../api';
import {currentPendingApproval} from '../../../../util/approval-workflow.utils';

@Component({
  selector: 'manage-ta-approval',
  imports: [
    Button,
    TaCostBubbleComponent,
    ReactiveFormsModule,
    Textarea,
    FormsModule,
    DatePipe,
    TaApprovalsTableComponent,
  ],
  templateUrl: './manage-ta-approval.component.html',
  styleUrl: './manage-ta-approval.component.scss'
})
export class ManageTaApprovalComponent extends ManageTaBaseComponent implements OnInit {
  approvalNotes = "";

  approvalRole = computed<'primaryApprover' | 'secondaryApprover' | 'viewOnly'>(() => {
    // Finance admins can approve/withhold/reject even when not the assigned approver.
    if (this.permissionService.canActionAnyApproval()){
      return 'primaryApprover';
    }
    if (this.permissionService.canApprove()) {
      if (this.taSubmission()?.secondaryApprover?.id === this.store.session().details?.user?.id &&
          this.taSubmission()?.primaryApprover?.id !== this.store.session().details?.user?.id
      ) {
        return 'secondaryApprover'
      }
      return 'primaryApprover'
    }
    return 'viewOnly';
  });
  // The approval slots this user owns - either assigned to them directly or to one of their groups.
  private userApprovals = computed<SubmissionApprovalDetails[]>(() => {
    const approvals = this.taSubmission()?.approvals ?? [];
    const userId = this.store.user()?.id;
    const userGroupCodes = new Set(this.permissionService.currentGroups().map(g => g.code).filter(Boolean));
    return approvals.filter(a =>
      (!!userId && a.approver?.id === userId) ||
      (a.groupApprovers?.some(ga => ga.code && userGroupCodes.has(ga.code)) ?? false)
    );
  });
  // The slot this user still has to action. An approver can hold two slots in the same chain
  // (Manager then Funding), so a slot they have already actioned must not mask a pending one.
  pendingApprovalForUser = computed(() => this.userApprovals().find(a => a.status === 'Pending'));
  // Drives the informational note only - the most recent slot of theirs that is done.
  priorActionedApproval = computed(() => {
    const actioned = this.userApprovals().filter(a => a.status === 'Actioned');
    return actioned.at(actioned.length - 1);
  });
  // The chain approval the TA currently waits on. Approvals are sequential, so an approver only
  // gets the action panel once every earlier stage is actioned (same object instance - both
  // computeds read the same approvals array, and the DTOs carry no id).
  currentApproval = computed(() => currentPendingApproval(this.taSubmission()?.approvals));
  isUsersTurn = computed<boolean>(() => {
    const mine = this.pendingApprovalForUser();
    return !!mine && mine === this.currentApproval();
  });
  isGroupApproval = computed(() => {
    const approval = this.priorActionedApproval();
    return !!approval && !approval.approver && !!approval.groupApprovers?.length;
  });
  showApprovalActions = computed<boolean>(() => {
    if (this.approvalRole() === 'viewOnly') {
      return false;
    }
    // Finance and System Admins may action any pending approval on the TA - the server falls
    // back to one (TAStore.ApproveSubmission / FindActionableApprovalForUser), so a slot they
    // have already actioned themselves must not hide the actions. This component only renders
    // while the TA is PendingApproval, so something is always still outstanding.
    if (this.permissionService.canActionAnyApproval()) {
      return true;
    }
    return this.isUsersTurn();
  });

  confirmApproved(action: 'approve' | 'withhold' | 'reject'): void {
    this.confirmationServiceWrapper.approveConfirm(action, this.approvalNotes, this.taSubmission()).subscribe({
      next: () => {
        this.goBack();
      },
      error: err => {
        console.error(err);
      },
      complete: () => {
        // console.log('Approve completed.');
      }
    });
  }
}
